import { Injectable, NgZone, inject } from "@angular/core";
import { Observable, Subject, forkJoin, throwError } from "rxjs";
import { BehaviorSubject } from "rxjs/internal/BehaviorSubject";
import {
    concatMap,
    debounceTime,
    filter,
    map,
    takeUntil
} from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { MapExtent } from "~app/common/components/map/map.models";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ArbetsorderViol3SynchronizationData } from "~app/common/events/saga/ArbetsorderViol3SynchronizationData";
import { ArbetsorderWorkTypeConstants, ConfirmDialogResponse } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { IBridgeEvent } from "~app/common/services/event-bridge.service";
import { EventHubService } from "~app/common/services/eventhub.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationErrorLevels } from "~app/common/validation/ValidationErrorLevels";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ValidationContext } from "~app/common/validation/validationContext";
import { IArbetsorderDetailResponse } from "~app/features/planera/model/IArbetsorderDetailResponse";
import {
    ArbetsorderChanged,
    ArbetsorderDetail,
    ArbetsorderDisabledFlag,
    ArbetsorderReloadProperties,
    ArbetsorderWrapper,
    IArbetsorderChanged,
    IArbetsorderDetail,
    IAtgardBarighetChanged,
    ISaveArbetsorderResponse
} from "~app/features/planera/planera-detail/planera-detail-models";
import { ArbetsorderDistriktUpdatedEventMessage } from "../events/ArbetsorderDistriktUpdatedEventMessage";
import { ArbetsorderDokumentUpdatedEventMessage } from "../events/ArbetsorderDokumentUpdatedEventMessage";
import { ArbetsorderHandelssortimentAddedEventMessage } from "../events/ArbetsorderHandelssortimentAddedEventMessage";
import { ArbetsorderHandelssortimentDeletedEventMessage } from "../events/ArbetsorderHandelssortimentDeletedEventMessage";
import { ArbetsorderOperationDeletedEvent } from "../events/ArbetsorderOperationDeletedEvent";
import { ArbetsorderOperationUpdatedEvent } from "../events/ArbetsorderOperationUpdatedEvent";
import { ArbetsorderPeriodsattPeriodUpdatedMessage } from "../events/ArbetsorderPeriodsattPeriodUpdatedMessage";
import { ArbetsorderRestriktionUpdatedEventMessage } from "../events/ArbetsorderRestriktionUpdatedEventMessage";
import { ArbetsorderWorkEventMessage } from "../events/ArbetsorderWorkEventMessage";
import { DestineringsalternativMergeCompletedEventMessage } from "../events/DestineringsalternativMergeCompletedEventMessage";
import { MapEvents } from "../events/map-events";
import { PlaneraKartaEventService } from "../planera-karta/planera-karta-event.service";
import { ArbetsorderInmemoryPropertyApplierService } from "./arbetsorder-inmemory-property-applier.service";
import { ArbetsorderMonitorService } from "./arbetsorder-monitor.service";
import { ArbetsorderSaveService } from "./arbetsorder-save.service";
import { PlaneraBridgeEvents, PlaneraEventService } from "./planera-event.service";
import { PlaneraService } from "./planera.service";

@Injectable({
    providedIn: "root"
})
export class ArbetsorderMediatorService {
    arbetsorder = new BehaviorSubject<IArbetsorderChanged>(null);
    checkAOsAfterSave: number[] = [];
    dirty = new BehaviorSubject<boolean>(null);
    documentUpdated$ = new BehaviorSubject<number>(null);
    readonly = new BehaviorSubject<boolean>(null);
    valid = new BehaviorSubject<boolean>(null);

    private readonly aoPropertyApplier = inject(ArbetsorderInmemoryPropertyApplierService);
    private readonly arbetsoderReloadPulse$ = new BehaviorSubject<ArbetsorderReloadProperties>(null);
    private readonly arbetsorderMonitorService = inject(ArbetsorderMonitorService);
    private readonly arbetsorderSaveService = inject(ArbetsorderSaveService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly cache: ArbetsorderWrapper[] = [];
    private readonly eventHubService = inject(EventHubService);
    private readonly eventService = inject(EventService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly ngZone = inject(NgZone);
    private readonly planeraEventService = inject(PlaneraEventService);
    private readonly planeraKartaBridgeEvents: IBridgeEvent[] = [];
    private readonly planeraKartaEventService = inject(PlaneraKartaEventService);
    private readonly planeraService = inject(PlaneraService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly validationErrors: string[];
    private readonly validationFactories: ((arbetsorder: IArbetsorderDetail) => ValidationContext)[];

    private readonly validationContextList: ValidationContext[] = [];

    private selectedId: number;

    constructor() {
        this.setupEvents();
        this.validationFactories = [];
        this.validationErrors = [];
        this.arbetsoderReloadPulse$
            .pipe(debounceTime(100))
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((value) => this.onArbetsoderReloadPulse(value));
    }

    atgardAddedToArbetsorder(payload: any): void {
        this.checkAOsAfterSave.push(payload.atgard.arbetsorderId);
    }

    checkUpdatedAOs() {
        // Kopierar för säkerhets skull över det till en temporär array
        // Kan vara så att andra events clearar detta under tiden
        const copyCheckAOsAfterSave = [...this.checkAOsAfterSave];
        if (copyCheckAOsAfterSave.length > 0) {
            copyCheckAOsAfterSave.forEach((x) => {
                this.planeraEventService.emit(PlaneraBridgeEvents.arbetsorderProbablyDeleted, { arbetsorderId: x });
            });
        }
        this.checkAOsAfterSave = [];
    }

    destroy(): void {
        this.arbetsorderMonitorService.stop();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.planeraKartaEventService.unsubscribeMany(this.planeraKartaBridgeEvents);
        this.planeraKartaBridgeEvents.splice(0);
        this.tryResetEvents(this.selectedId, null);
        this.selectedId = null;
    }

    extentUpdated(payload: any): void {
        const ao = this.getFromCache(this.selectedId);
        ao.arbetsorder.commandManager.updateCommand(payload);
        this.setDirty();
    }

    geometryRemoved(payload: any): void {
        const geometri = payload.geometri;
        const ao = this.getFromCache(this.selectedId);
        const currentIndex = ao.arbetsorder.kartgeometrier.findIndex(e => e.id === geometri.id);
        if (currentIndex > -1) {
            ao.arbetsorder.kartgeometrier.splice(currentIndex, 1);
            this.setDirty(true);
        }
    }

    geometryUpdated(payload: any): void {
        this.geometryRemoved(payload);
        const ao = this.getFromCache(this.selectedId);
        ao?.arbetsorder.kartgeometrier.push(payload.geometri);
        this.setDirty(true);
    }

    getErrors(): string[] {
        return this.validationErrors;
    }

    load(arbetsorderid: number, showBusy = true): void {
        const work$ = this.planeraService.getArbetsorderDetail(arbetsorderid);
        const next = (arbetsorderDetail: IArbetsorderDetailResponse) => {
            const copy = new ArbetsorderDetail(arbetsorderDetail.arbetsorder);
            this.setArbetsorder(copy);
            this.arbetsorderMonitorService.refreshAlerts();
        };
        const error = (err: any) => {
            // Gick inte att ladda arbetsordern. Troligtvis för att den är borttagen. Eller annan fel.
            // Om den är borttagen så hanteras det av subscribers på detta event
            this.planeraEventService.emit(PlaneraBridgeEvents.arbetsorderProbablyDeleted, { arbetsorderId: arbetsorderid });
        };

        if (showBusy) {
            this.busyService.addWork(work$).subscribe({
                next: next,
                error: error
            });
        } else {
            work$.subscribe({
                next: next,
                error: error
            });
        }
    }

    mapPan(payload: any): void {
        if (ObjectUtils.hasValue(this.selectedId) && this.selectedId === payload.arbetsorderId) {
            const ao = this.getFromCache(this.selectedId);
            ao.metadata.extent = new MapExtent(payload.minX, payload.minY, payload.maxX, payload.maxY); //update extent
        } else {
            // no current arbetsorder
        }
    }

    registerValidationFactory(factory: (arbetsorder: IArbetsorderDetail) => ValidationContext) {
        this.validationFactories.push(factory);
        this.validate();
    }

    addValidationContext(validationContext: ValidationContext) {
        this.validationContextList.push(validationContext);
        validationContext.current.rules.forEach(x => x.changed
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(y => {
                this.validationErrors.splice(0);
                this.valid.next(this.validationContextList.filter(z => z.hasErrors(ValidationScopes.spara)).length === 0);
                this.validationContextList.forEach(scope => this.validationErrors.push(...scope.current.contextAccessor().errors));
            }));
    }
    reload(withWarning = false): void {
        if (!this.selectedId) {
            return;
        }
        if (withWarning && this.dirty.value) {
            this.warnReload().subscribe(result => {
                if (result === ConfirmDialogResponse.save) {
                    this.load(this.selectedId, true);
                }
            });
        } else {
            this.load(this.selectedId, false);
        }
    }

    reset(): void {
        this.destroy();
        this.setupEvents();
    }

    save(barighetChanged: IAtgardBarighetChanged[] = null, showBusy = true, callback?: (IArbetsorderDetail) => void): Observable<ISaveArbetsorderResponse> {
        return this.validate().pipe(concatMap(() => {
            if (this.valid.value === false) {
                return throwError(() => "");
            } else {
                const aoWrapper = this.getFromCache(this.selectedId);
                const arbetsorder = aoWrapper.arbetsorder;
                const save = this.arbetsorderSaveService.save(arbetsorder, barighetChanged, showBusy);
                save.subscribe(result => {
                    this.checkUpdatedAOs();
                    arbetsorder.commandManager.destroy();
                    if (arbetsorder.commandManager) {
                        arbetsorder.commandManager = null;
                    }

                    const copy = new ArbetsorderDetail(result.currentArbetsorder);
                    this.setArbetsorder(copy);
                    this.arbetsorderMonitorService.refreshAlerts();
                    this.planeraKartaEventService.emit(MapEvents.arbetsorderAfterSave, null);
                    if (barighetChanged != null) {
                        barighetChanged.splice(0);
                    }
                    if (callback) {
                        callback(copy);
                    }
                });
                return save;
            }
        }));
    }

    setArbetsorder(newAo: IArbetsorderDetail): void {
        this.validationFactories.splice(0);
        this.validationErrors.splice(0);
        this.validationContextList.splice(0);
        const curr = this.getFromCache(this.selectedId);
        const currArbetsorderId = curr?.arbetsorder?.id;
        const currArbetsorderRowVersion = curr?.arbetsorder?.rowVersion;
        this.tryResetEvents(this.selectedId, newAo?.id);
        const ao = this.setInternal(newAo);
        this.arbetsorder.next(new ArbetsorderChanged(ao, currArbetsorderId, currArbetsorderRowVersion));
        if (ao) {
            this.readonly.next((this.canEdit() && ao?.arbetsorder?.isEditableForMode(ArbetsorderDisabledFlag.makulerad)) === false);
        } else {
            this.readonly.next(true);
        }
        this.setPristine();
        this.validate();
    }

    setDirty(overideCommandCheck = false): void {
        if (overideCommandCheck) {
            this.dirty.next(true);
            return;
        }
        const ao = this.getFromCache(this.selectedId);
        this.dirty.next(ao.arbetsorder.commandManager.isDirty());

    }

    setPristine(): void {
        this.checkAOsAfterSave = [];
        this.dirty.next(false);
    }

    utbytesSimulera(shouldSaveBefore: boolean): void {
        const simulera = (id: number, version: string) => this.planeraService.performUtbytessimulering(id, version).pipe(map(result => {
            const copy = new ArbetsorderDetail(result.arbetsorder);
            this.setArbetsorder(copy);
            return result;
        }));

        let work$: Observable<any>;

        if (shouldSaveBefore === true) {
            work$ = this.save(null, false).pipe(concatMap(savedAO =>
                simulera(savedAO.currentArbetsorder.id, savedAO.currentArbetsorder.rowVersion)
            ));
        } else {
            const ao = this.getFromCache(this.selectedId);
            work$ = simulera(ao.id, ao.arbetsorder.rowVersion);
        }

        this.busyService.addWork(work$).subscribe();
    }

    private canEdit(): boolean {
        return this.authenticationService.canEdit();
    }

    private createOrUpdateCache(arbetsorder: IArbetsorderDetail): ArbetsorderWrapper {
        if (!arbetsorder) {
            return null;
        }
        if (this.cache.some(ao => ao.id === arbetsorder.id)) {
            const a = this.cache.find(ao => ao.id === arbetsorder.id);
            a.setArbetsorder(arbetsorder);
            return a;
        } else {
            const defaultExtent = arbetsorder.extent;
            const metadata = {
                id: arbetsorder.id,
                extent: defaultExtent ? new MapExtent(defaultExtent?.minX, defaultExtent?.minY, defaultExtent?.maxX, defaultExtent?.maxY) : null
            };
            const ao = new ArbetsorderWrapper(
                arbetsorder,
                metadata);
            this.cache.push(ao);
            return ao;
        }
    }

    private getFromCache(arbetsorderid: number): ArbetsorderWrapper {
        return this.cache.find(ao => ao.id === arbetsorderid) ?? null;
    }

    private onArbetsoderReloadPulse(value: ArbetsorderReloadProperties) {
        if (ObjectUtils.hasValue(value)) {
            this.reload(this.arbetsoderReloadPulse$.value.withWarning);
        }
    }

    private setInternal(arbetsorder: IArbetsorderDetail): ArbetsorderWrapper {
        this.selectedId = arbetsorder?.id;
        const ao = this.createOrUpdateCache(arbetsorder);
        if (ao?.arbetsorder) {
            ao.arbetsorder.commandManager.onCommandReceived(() => this.setDirty());
            // "adjust date"
            if (ao?.arbetsorder?.preliminartStartdatum !== null) {
                ao.arbetsorder.preliminartStartdatum = new Date(ao.arbetsorder.preliminartStartdatum.toString());
            }
        }
        return ao;
    }

    private setupEvents(): void {
        const ao = this.getFromCache(this.selectedId);

        this.eventService.subscribeEvent<ArbetsorderOperationUpdatedEvent>(ArbetsorderOperationUpdatedEvent.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(ev => this.aoPropertyApplier.handleOperationUpdatedEvent(ao?.arbetsorder, ev));

        this.eventService.subscribeEvent<ArbetsorderOperationDeletedEvent>(ArbetsorderOperationDeletedEvent.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(ev => this.aoPropertyApplier.handleOperationDeletedEvent(ao?.arbetsorder, ev));

        this.eventService.subscribeEvent<ArbetsorderViol3SynchronizationData>(ArbetsorderViol3SynchronizationData.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(ev => this.aoPropertyApplier.handleArbetsorderViol3SynchronizationData(ao?.arbetsorder, ev));

        this.eventService.subscribeEvent<ArbetsorderDistriktUpdatedEventMessage>(ArbetsorderDistriktUpdatedEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(ev => {
                this.reload(true);
            });

        this.eventService.subscribeEvent<ArbetsorderRestriktionUpdatedEventMessage>(ArbetsorderRestriktionUpdatedEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(ev => {
                this.reload(true);
            });

        this.eventService
            .subscribeEvent<DestineringsalternativMergeCompletedEventMessage>(DestineringsalternativMergeCompletedEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                this.aoPropertyApplier.handleDestineringsalternativMergeCompletedEvent(ao?.arbetsorder, event);
            });

        this.eventService
            .subscribeEvent<ArbetsorderHandelssortimentDeletedEventMessage>(ArbetsorderHandelssortimentDeletedEventMessage.$type)
            .pipe(debounceTime(100))
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                this.arbetsoderReloadPulse$.next(new ArbetsorderReloadProperties(true));
            });

        this.eventService
            .subscribeEvent<ArbetsorderHandelssortimentAddedEventMessage>(ArbetsorderHandelssortimentAddedEventMessage.$type)
            .pipe(debounceTime(100))
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                this.arbetsoderReloadPulse$.next(new ArbetsorderReloadProperties(true));
            });

        this.eventService.subscribeEvent<ArbetsorderPeriodsattPeriodUpdatedMessage>(ArbetsorderPeriodsattPeriodUpdatedMessage.$type)
            .pipe(debounceTime(100))
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.reload(true);
            });

        this.eventService.subscribeEvent<ArbetsorderWorkEventMessage>(ArbetsorderWorkEventMessage.$type)
            .pipe(filter(x => x.arbetsorderId === this.selectedId && x.work === ArbetsorderWorkTypeConstants.utbytessimulera && x.status === ArbetsorderWorkEventMessage.completed))
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.arbetsoderReloadPulse$.next(new ArbetsorderReloadProperties(true));
            });

        this.eventService
            .subscribeEvent<ArbetsorderDokumentUpdatedEventMessage>(ArbetsorderDokumentUpdatedEventMessage.$type)
            .pipe(debounceTime(1000))
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((x: ArbetsorderDokumentUpdatedEventMessage) => {
                if (x.arbetsorderId === this.selectedId) {
                    this.documentUpdated$.next(x.arbetsorderId);
                }
            });

        this.planeraKartaBridgeEvents.push(this.planeraKartaEventService.subscribe(MapEvents.geometriUpdated, (payload) => this.geometryUpdated(payload)));
        this.planeraKartaBridgeEvents.push(this.planeraKartaEventService.subscribe(MapEvents.geometriRemoved, (payload) => this.geometryRemoved(payload)));
        this.planeraKartaBridgeEvents.push(this.planeraKartaEventService.subscribe(MapEvents.arbetsorderExtentUpdated, (payload) => this.extentUpdated(payload)));
        this.planeraKartaBridgeEvents.push(this.planeraKartaEventService.subscribe(MapEvents.addAtgardToArbetsorder, (payload) => this.atgardAddedToArbetsorder(payload as any)));
        this.planeraKartaBridgeEvents.push(this.planeraKartaEventService.subscribe(MapEvents.mapPan, (payload) => this.mapPan(payload)));
    }

    private tryResetEvents(previousId?: number, nextId?: number): void {
        if (previousId && previousId !== nextId) {
            this.ngZone.runOutsideAngular(() => {
                setTimeout(() => {
                    this.eventHubService.unregisterListener(`arbetsorder/${previousId}`);
                }, 180000);
            });
        }
        if (nextId && previousId !== nextId) {
            this.eventHubService.registerListener(`arbetsorder/${nextId.toString()}`);
            this.arbetsorderMonitorService.start(nextId);
        }
    }

    private validate(): Observable<void> {
        let valid = true;
        this.validationErrors.splice(0);

        const ao = this.getFromCache(this.selectedId);
        if (!ao) {
            // no arbetsorder yet
            return undefined;
        }

        const observableContexts = this.validationFactories.map(func => func(ao.arbetsorder).scope(ValidationScopes.spara, ValidationErrorLevels.error));

        return forkJoin([...observableContexts]).pipe(map(scopes => {
            valid = !(scopes.some(scope => scope.contextAccessor().validate() === false));
            scopes.forEach(scope => this.validationErrors.push(...scope.contextAccessor().errors));
            this.valid.next(valid);
        }));
    }

    private warnReload(): Observable<any> {
        return this.raskModalService.confirm(new ModalSettings("Arbetsordern har uppdaterats",
            "Arbetsordern har uppdaterats och behöver laddas om innan eventuella ändringar kan sparas"));
    }
}
